import React from 'react';

interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg';
  variant?: 'primary' | 'secondary' | 'light' | 'dark';
  text?: string;
  className?: string;
  centered?: boolean;
}

const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = 'md',
  variant = 'primary',
  text,
  className = '',
  centered = false,
}) => {
  const hasText = typeof text === 'string' && text.trim().length > 0;
  const isExplicitEmptyText = typeof text === 'string' && text.trim().length === 0;
  const effectiveText = hasText ? text : 'Loading...';

  const variantClass = (() => {
    switch (variant) {
      case 'secondary':
        return 'text-secondary';
      case 'light':
        return 'text-light';
      case 'dark':
        return 'text-dark';
      case 'primary':
      default:
        return 'text-primary';
    }
  })();

  const containerClasses = [
    'd-flex',
    centered ? 'justify-content-center align-items-center' : 'align-items-center',
    centered && hasText ? 'flex-column' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const spinnerClassNames = [
    'spinner-border',
    size === 'sm' ? 'spinner-border-sm' : '',
    size === 'lg' ? 'loading-spinner-lg' : '',
    variantClass,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={containerClasses}>
      <div
        data-testid="loading-spinner"
        className={spinnerClassNames}
        role="status"
        aria-live="polite"
        aria-hidden={isExplicitEmptyText ? 'false' : 'true'}
      >
      {!hasText && <span className="visually-hidden">{effectiveText}</span>}
      </div>

      {(hasText || isExplicitEmptyText) && (
        <span className="loading-text">{effectiveText}</span>
      )}
    </div>
  );
};

export default LoadingSpinner;